import React, { useEffect, useRef, useState } from "react";
import { askSean, API } from "@/lib/api";
import { scriptedReply } from "@/lib/scriptedBrain";

const SEAN_AVATAR = "/sean-portrait-new.jpg";

/**
 * Floating "Sean" chatbot.
 *   - Avatar pill bottom-right (Sean's real headshot)
 *   - Click → opens a small chat panel anchored bottom-right
 *   - Sends messages to /api/ask-sean (Claude Sonnet 4.5)
 *   - Persists session_id per browser so the bot remembers context
 */
export default function SeanChatbot() {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false); // hide the "Hi!" bubble after first click
  // Auto-dismiss the welcome bubble after a few seconds so it never permanently overlaps content
  useEffect(() => {
    if (seen) return;
    const t = setTimeout(() => setSeen(true), 8000);
    return () => clearTimeout(t);
  }, [seen]);
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem("seanlab_floatchat");
    if (!sid) {
      sid = `float-${Math.random().toString(36).slice(2)}-${Date.now()}`;
      localStorage.setItem("seanlab_floatchat", sid);
    }
    return sid;
  });
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "Hey Sciencer! 🧡 I'm Sean — my AI is called Atitsogbe Intelligence. Type, or tap the 🎙 mic to ask me out loud. Books, brains, hearts, basilisk lizards, you name it.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const scrollRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const recStreamRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages]);

  // Track the mobile keyboard via visualViewport so the panel resizes instead of getting hidden under it
  const [kbInset, setKbInset] = useState(0);
  useEffect(() => {
    if (!open || typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onResize = () => {
      // distance between window inner height and visual viewport = keyboard height
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbInset(inset);
    };
    onResize();
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
      setKbInset(0);
    };
  }, [open]);

  // Lock background page scroll while the chat panel is open
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
      touchAction: body.style.touchAction,
      overscroll: body.style.overscrollBehavior,
    };
    // CSS-only on desktop; iOS-safe technique on touch devices
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    body.style.touchAction = "none";
    body.style.overscrollBehavior = "none";
    // iOS Safari needs position:fixed to fully block scroll
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      html.style.overflow = prev.htmlOverflow;
      body.style.touchAction = prev.touchAction;
      body.style.overscrollBehavior = prev.overscroll;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);

    // 🧠 Local intent: route booking / merch / website questions to the right place
    const lower = text.toLowerCase();
    const bookingKeywords = ["book sean", "booking", "hire", "appearance", "appear", "speak at", "speak to", "keynote", "event", "school visit", "fee", "price", "cost", "contract", "agreement"];
    const merchKeywords   = ["merch", "shop", "store", "t-shirt", "tshirt", "hoodie", "sticker", "buy", "purchase", "swag", "tee ", " tee", "apparel"];
    const siteKeywords    = ["website", "site", "official site", "seanthesciencekid", "homepage", "home page", "press kit", "news"];
    const vaultKeywords   = ["vault", "secret", "sneak peek", "sneak-peek", "upcoming", "what are you working on", "new project", "next project", "iq puzzle", "puzzle"];

    const askedAboutBooking = bookingKeywords.some((kw) => lower.includes(kw));
    const askedAboutMerch   = merchKeywords.some((kw) => lower.includes(kw));
    const askedAboutSite    = siteKeywords.some((kw) => lower.includes(kw));
    const askedAboutVault   = vaultKeywords.some((kw) => lower.includes(kw));

    let tail = "";
    if (askedAboutBooking) {
      setTimeout(() => {
        try {
          const section = document.getElementById("bookings");
          if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
          const toggle = document.querySelector('[data-testid="bookings-toggle"]');
          const body = document.querySelector('[data-testid="bookings-body"]');
          if (toggle && !body) toggle.click();
        } catch {/* noop */}
      }, 400);
      tail = "\n\n👇 Just opened my Booking Agreement at the bottom of the page — fill out the form to lock in your event!";
    } else if (askedAboutMerch) {
      setTimeout(() => {
        try { document.getElementById("sean-world")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch {/* noop */}
      }, 400);
      tail = "\n\n🛒 Grab Sciencer swag here → https://sean-the-science-kid.dashery.com/ (I just scrolled the page to my shop for you!)";
    } else if (askedAboutSite) {
      tail = "\n\n🧡 Visit my home base → https://seanthesciencekid.net/";
    } else if (askedAboutVault) {
      tail = "\n\n🤫 Want a peek at what I'm cooking up next? Scroll to the footer and tap **🔓 The Vault**. Three puzzles. Solve them and you're in. 🧠";
    }

    try {
      // 🧠 Scripted brain — kid-safe, zero API cost, no rate limits.
      // Replaces the paid LLM call with a curated keyword-matching library.
      const reply = scriptedReply(text);
      // Simulate a tiny "thinking" delay so the response doesn't feel instant/robotic
      await new Promise((r) => setTimeout(r, 250 + Math.random() * 350));
      setMessages((m) => [...m, { role: "assistant", text: reply + tail }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "⚠ Couldn't reach the lab right now. Try once more!" },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ============== Mic / voice input ==============
  const pickAudioMime = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
    if (typeof MediaRecorder === "undefined") return "";
    for (const m of candidates) {
      try { if (MediaRecorder.isTypeSupported(m)) return m; } catch {/* noop */}
    }
    return "";
  };

  const startMic = async () => {
    setVoiceError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceError("Your browser doesn't support voice input. Try Chrome or Edge.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      const mime = pickAudioMime();
      let rec;
      try { rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined); }
      catch { rec = new MediaRecorder(stream); }
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        recStreamRef.current = null;
        if (blob.size < 800) {
          setVoiceError("That was too short — try again and hold the mic for a sec longer.");
          return;
        }
        await uploadAndSend(blob);
      };
      recRef.current = rec;
      rec.start(500);
      setRecording(true);
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setVoiceError("🚫 Mic permission blocked. Allow it in your browser settings.");
      } else if (name === "NotFoundError") {
        setVoiceError("🔌 No microphone found on this device.");
      } else {
        setVoiceError(`Couldn't access mic: ${e?.message || "unknown error"}`);
      }
    }
  };

  const stopMic = () => {
    try { recRef.current?.stop(); } catch {/* noop */}
    setRecording(false);
  };

  const cancelMic = () => {
    try { recRef.current && (recRef.current.onstop = null) && recRef.current.stop(); } catch {/* noop */}
    try { recStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {/* noop */}
    recStreamRef.current = null;
    chunksRef.current = [];
    setRecording(false);
  };

  const uploadAndSend = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      const ext = (blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm");
      fd.append("audio", blob, `voice.${ext}`);
      const res = await fetch(`${API}/voice/transcribe`, { method: "POST", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const { text } = await res.json();
      const cleaned = (text || "").trim();
      if (!cleaned) {
        setVoiceError("I couldn't quite hear that — give it another shot!");
        return;
      }
      // Send the transcribed text just like a typed message
      await send(cleaned);
    } catch (e) {
      setVoiceError(`Couldn't transcribe: ${e?.message || "try again"}`);
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <div
          className="fixed z-40 no-print"
          style={{ right: 16, bottom: 18 }}
        >
          {!seen && (
            <div
              className="card-flat bg-white absolute"
              style={{
                right: 76,
                bottom: 8,
                padding: "0.55rem 0.85rem",
                whiteSpace: "nowrap",
                fontSize: "0.85rem",
              }}
              data-testid="sean-bubble-hi"
            >
              Hi! Ask me anything ⚡
              <div
                style={{
                  position: "absolute", right: -7, top: "50%", transform: "translateY(-50%)",
                  width: 0, height: 0,
                  borderTop: "8px solid transparent",
                  borderBottom: "8px solid transparent",
                  borderLeft: "8px solid var(--ink)",
                }}
              />
            </div>
          )}
          <button
            onClick={() => { setOpen(true); setSeen(true); }}
            data-testid="sean-chatbot-launcher"
            aria-label="Open Atitsogbe Intelligence chat"
            className="relative animate-float"
            style={{
              width: 64, height: 64, borderRadius: "50%",
              border: "3px solid var(--ink)",
              boxShadow: "4px 4px 0 0 var(--ink)",
              background: "var(--orange)",
              overflow: "hidden",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <img
              src={SEAN_AVATAR}
              alt="Chat with Sean"
              className="w-full h-full object-cover block"
              referrerPolicy="no-referrer"
            />
            <span
              className="absolute"
              style={{
                right: -2, top: -2, background: "var(--orange)", color: "#fff",
                fontSize: "0.6rem", padding: "2px 6px", borderRadius: 999,
                border: "2px solid var(--ink)", fontWeight: 800,
                letterSpacing: "0.04em",
              }}
            >
              AT.I
            </span>
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed z-50 no-print sean-chat-panel"
          data-testid="sean-chat-panel"
          style={{ bottom: 12 + kbInset }}
        >
          <div className="card bg-white sean-chat-card" style={{ padding: 0, overflow: "hidden" }}>
            <div
              className="flex items-center gap-3 px-4 py-3"
              style={{ background: "var(--orange)", borderBottom: "2.5px solid var(--ink)", color: "#fff" }}
            >
              <img
                src={SEAN_AVATAR}
                alt="Sean"
                className="rounded-full block"
                style={{ width: 42, height: 42, border: "2.5px solid var(--ink)", objectFit: "cover", background: "#fff" }}
              />
              <div className="leading-tight flex-1 min-w-0">
                <div className="font-display text-lg" style={{ color: "#fff" }}>Sean</div>
                <div className="text-[0.68rem] font-mono" style={{ color: "rgba(255,255,255,0.85)" }}>
                  ● Atitsogbe Intelligence
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                data-testid="sean-chat-close"
                className="w-8 h-8 rounded-full bg-white border-[2.5px] border-[var(--ink)] flex items-center justify-center text-xl font-bold leading-none"
                style={{ boxShadow: "2px 2px 0 0 var(--ink)", color: "var(--ink)" }}
              >
                ×
              </button>
            </div>

            <div
              ref={scrollRef}
              className="px-3 py-3 space-y-2.5 sean-chat-scroll"
              data-testid="sean-chat-scroll"
            >
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} text={m.text} />
              ))}
              {sending && <Bubble role="assistant" text="…" />}
            </div>

            {messages.length <= 1 && !sending && !inputFocused && input.length === 0 && (
              <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="sean-chat-chips">
                {[
                  "How do I book Sean?",
                  "Where can I buy merch?",
                  "What's the Neurocardio Loop?",
                  "Recommend a book for me",
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="pill"
                    style={{ background: "var(--paper-2)", fontSize: "0.7rem", padding: "0.3rem 0.7rem", cursor: "pointer" }}
                    data-testid={`chat-chip-${q.slice(0,8).replace(/\s+/g,'-').toLowerCase()}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {voiceError && (
              <div className="px-3 pb-2 text-xs font-bold text-[var(--red)]" data-testid="sean-voice-error">
                {voiceError}
              </div>
            )}

            <div
              className="p-3 flex items-center gap-2"
              style={{ borderTop: "2.5px solid var(--ink)", background: "var(--paper-2)" }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                placeholder={recording ? "🔴 Recording… tap mic to send" : (transcribing ? "Transcribing…" : "Ask Sean anything…")}
                className="field flex-1"
                data-testid="sean-chat-input"
                disabled={recording || transcribing}
                enterKeyHint="send"
                inputMode="text"
              />
              <button
                onClick={recording ? stopMic : startMic}
                disabled={sending || transcribing}
                aria-label={recording ? "Stop recording and send" : "Ask Sean by voice"}
                title={recording ? "Stop & send" : "Ask by voice"}
                data-testid="sean-chat-mic"
                className="btn"
                style={{
                  background: recording ? "var(--red)" : "var(--orange)",
                  color: "#fff",
                  padding: "0.55rem 0.7rem",
                  minWidth: 44,
                }}
              >
                {recording ? "⏹" : (transcribing ? "…" : "🎙")}
              </button>
              <button
                onClick={() => send()}
                disabled={sending || transcribing || recording || !input.trim()}
                className="btn btn-red"
                style={{ padding: "0.55rem 0.95rem" }}
                data-testid="sean-chat-send"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .sean-chat-panel { right: 12px; width: calc(100% - 24px); max-width: 380px; }
        .sean-chat-card  { display: flex; flex-direction: column; max-height: min(640px, calc(100dvh - 24px)); }
        .sean-chat-scroll { overflow-y: auto; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; flex: 1; min-height: 180px; max-height: 50vh; }
        @media (min-width: 640px) {
          .sean-chat-panel { right: 18px; }
        }
      `}</style>
    </>
  );
}

function Bubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className="px-3 py-2 text-sm leading-snug"
        style={{
          background: isUser ? "var(--sky)" : "#fff",
          color: "var(--ink)",
          border: "2px solid var(--ink)",
          borderRadius: 14,
          maxWidth: "85%",
          boxShadow: "2px 2px 0 0 var(--ink)",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text}
      </div>
    </div>
  );
}
